import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@store/hooks'
import { useBetaMode } from '@hooks/useBetaMode'
import { isAdminPanelRole } from '@utils/adminPermissions'
import { trackFeature } from '@utils/trackFeature'
import { getMoreNavItems } from './appNavConfig'
import { isNavItemActive } from './sidebarUtils'

type MoreSheetProps = {
  open: boolean
  onClose: () => void
}

export function MoreSheet({ open, onClose }: MoreSheetProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { beta } = useBetaMode()
  const isAdmin = isAdminPanelRole(user?.role)
  const items = getMoreNavItems({ beta, isAdmin })

  const handleSelect = (path: string, analyticsId: string) => {
    trackFeature('nav_more_item', { item: analyticsId })
    onClose()
    if (location.pathname !== path) {
      navigate(path)
    }
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: 'min(70vh, 520px)',
          pb: 'env(safe-area-inset-bottom, 0px)',
        },
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" component="h2" fontWeight={600}>
          More
        </Typography>
      </Box>
      <List disablePadding component="nav" aria-label="More navigation">
        {items.map((item) => {
          const active = isNavItemActive(item.path, location.pathname)
          const analyticsId = item.analyticsId ?? item.text.toLowerCase().replace(/\s+/g, '_')
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={active}
                onClick={() => handleSelect(item.path, analyticsId)}
                sx={{ minHeight: 48 }}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <item.Icon />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Drawer>
  )
}

export default MoreSheet
